import React, { useState, useMemo, useRef } from "react";
import { Button, Flex, Input } from "antd";
import * as FaIcons from "react-icons/fa"; // Import all Font Awesome icons
import Popup, { IPopup } from "./Popup";
import debounce from "lodash.debounce";

interface IconSelectorProps {
  onSelect: (iconName: string) => void;
  value?: string;
  openTrigger: React.ReactNode;
}

const IconSelector: React.FC<IconSelectorProps> = ({
  onSelect,
  value,
  openTrigger,
}) => {
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const modalRef = useRef<IPopup>(null);
  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchQuery(query);
      }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  // Map through the FaIcons object to get the list of icons
  const iconEntries = Object.entries(FaIcons) as [
    string,
    React.FC<React.SVGProps<SVGSVGElement>>
  ][];

  // Filter icons based on search query
  const filteredIcons = useMemo(
    () =>
      iconEntries.filter(([iconName]) =>
        iconName.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [iconEntries, searchQuery]
  );

  const handleIconClick = (iconName: string) => {
    setSelectedIcon(iconName);
    onSelect(iconName);
  };

  function handleDone() {
    if (!selectedIcon) return;
    onSelect(selectedIcon);
    setSelectedIcon(null);
    setSearchQuery("");
    modalRef.current?.close();
  }

  return (
    <Popup ref={modalRef} openTrigger={openTrigger}>
      <Input
        placeholder="Search Icon"
        style={{ marginTop: 30, marginBottom: 10 }}
        onChange={handleSearchChange}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          height: "50vh",
          overflow: "auto",
          alignItems: "flex-start",
        }}
      >
        {!filteredIcons?.length && <h3>No Icon Found</h3>}
        {filteredIcons.map(([iconName, IconComponent]) => (
          <div
            key={iconName}
            onClick={() => handleIconClick(iconName)}
            style={{
              margin: 10,
              cursor: "pointer",
              border:
                selectedIcon === iconName
                  ? "1px solid #EC171E"
                  : "1px solid rgba(255,255,255,0.08)",
              background:
                selectedIcon === iconName
                  ? "rgba(236,23,30,0.14)"
                  : "rgba(255,255,255,0.04)",
              padding: 10,
              borderRadius: 10,
              textAlign: "center",
              flex: 1,
            }}
          >
            <IconComponent />
            <div>{iconName}</div>
          </div>
        ))}
      </div>
      <Flex gap={10} justify="end" style={{ marginTop: 20 }}>
        <Button type="text">Cancel</Button>
        <Button type="primary" onClick={() => handleDone()}>
          Done
        </Button>
      </Flex>
    </Popup>
  );
};

export default IconSelector;
